import { Field, Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { InputField } from '../../../components/formFields/InputField';
import { SelectFormField } from '../../../components/formFields/SelectFormField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { apiService } from '../../../utils/api';
import FormItemWithCheckbox from '../features/FormItemWithCheckbox';
import useGetDocument from '../hooks/getDocument';
import * as Yup from 'yup';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import useGetInheritedValues from '../hooks/getInheritedValues';

interface PartModalProps {
  formProps?: any;
  onSubmit: (id: any, values: any) => any;
  visible: boolean;
  documentId?: any;
  documentationId: any;
  onClose: () => void;
}

const PartModal: React.FC<PartModalProps> = ({
  formProps,
  onSubmit,
  visible,
  documentId,
  documentationId,
  onClose,
}) => {
  const formRefs = useRef<any>();
  const { data } = useGetDocument(formProps?.id ?? documentId);
  const { data: inherited } = useGetInheritedValues(
    documentationId,
    formProps?.parent ?? documentId
  );

  const anyData = data as any;
  const initialState = {
    reference_number: anyData?.reference_number ?? null,
    title: anyData?.title ?? null,
    chief_engineer_name: anyData?.chief_engineer_name ?? null,
    builder_name: anyData?.builder_name ?? null,
    processor: anyData?.processor ?? null,
    profession: anyData?.profession ?? null,
    document_type: anyData?.document_type ?? null,
  };

  const [documentTypes, setDocumentTypes] = useState<any[]>([]);

  useEffect(() => {
    const getDocumentTypes = async () => {
      try {
        const { data } = await apiService.get('/api/v1/document/types/');
        const options = data.map(({ title, shortcut }: { title: string; shortcut: string }) => ({
          id: shortcut,
          title,
        }));
        setDocumentTypes(options);
      } catch {
        //
      }
    };

    getDocumentTypes();
  }, []);

  const ValidationSchema = Yup.object().shape({
    document_type: Yup.string().nullable().required('Povinné pole'),
    title: Yup.string().nullable().required('Povinné pole'),
  });

  if (documentId && !data) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <SpinnerOverlay />
      </div>
    );
  }

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{formProps ? 'Editace dokumentu' : 'Vytvoření dokumentu'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRefs}
            enableReinitialize={true}
            initialValues={documentId ? ({} as any) : initialState}
            validationSchema={ValidationSchema}
            onSubmit={(values, { setErrors }) => {
              const errors = onSubmit(formProps ? formProps.id : null, values);
              if (errors) {
                setErrors(errors);
              }
            }}
          >
            {(props) => {
              const { values, setFieldValue } = props;

              return (
                <Form className="space-y-1">
                  <Field
                    name="title"
                    value={values.title || undefined}
                    placeholder=""
                    label="Název"
                    component={InputField}
                  />

                  <SelectFormField
                    name="document_type"
                    placeholder=""
                    label="Typ dokumentu"
                    data={documentTypes}
                    disabled={formProps?.document_type === 'SH'}
                  />

                  {values.document_type === 'SH' && (
                    <FormItemWithCheckbox
                      label="Číslo výkresu"
                      name="reference_number"
                      placeholder=""
                      component={InputField}
                      value={values.reference_number || undefined}
                      inherited={inherited?.reference_number}
                      onChangeParent={(val) => {
                        console.log({ val });
                        setFieldValue('reference_number', val);
                      }}
                    />
                  )}

                  <FormItemWithCheckbox
                    label="Stavebník"
                    name="builder_name"
                    placeholder=""
                    component={InputField}
                    value={values.builder_name || undefined}
                    inherited={inherited?.inherited?.builder_name}
                    onChangeParent={(val) => {
                      setFieldValue('builder_name', val);
                    }}
                  />
                  <FormItemWithCheckbox
                    label="Hlavní inženýr projektu"
                    name="chief_engineer_name"
                    placeholder=""
                    component={InputField}
                    value={values.chief_engineer_name || undefined}
                    inherited={inherited?.inherited?.chief_engineer_name}
                    onChangeParent={(val) => {
                      setFieldValue('chief_engineer_name', val);
                    }}
                  />
                  <FormItemWithCheckbox
                    label="Zpracovatel objektu"
                    name="processor"
                    placeholder=""
                    component={InputField}
                    value={values.processor || undefined}
                    inherited={inherited?.inherited?.processor}
                    onChangeParent={(val) => {
                      setFieldValue('processor', val);
                    }}
                  />
                  <FormItemWithCheckbox
                    label="Profese"
                    name="profession"
                    placeholder=""
                    component={InputField}
                    value={values.profession || undefined}
                    inherited={inherited?.inherited?.profession}
                    onChangeParent={(val) => {
                      setFieldValue('profession', val);
                    }}
                  />
                </Form>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            data-testid="part-modal-close-btn"
            onClick={() => onClose()}
          >
            Zavřít
          </Button>
          <Button
            type="submit"
            key={2}
            data-testid="part-modal-confirm-btn"
            onClick={() => formRefs.current?.submitForm()}
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PartModal;
